import { Monitor, Globe, User, Cpu, Server, ShieldOff, Lock } from 'lucide-react';
import { GraphNode } from '../EntityGraph';
import { ColumnDef, DraggableTh, useColumnPrefs } from '../ColumnPicker';

const TYPE_ICON = { host: Monitor, ip: Globe, user: User, process: Cpu, device: Server };
const TYPE_LABEL: Record<GraphNode['type'], string> = {
  host: 'Host', ip: 'IP address', user: 'User account', process: 'Process / application', device: 'Security device',
};

const ENTITY_COLUMNS: ColumnDef[] = [
  { key: 'entity', label: 'Entity', defaultVisible: true },
  { key: 'type', label: 'Type', defaultVisible: true },
  { key: 'exposure', label: 'Exposure', defaultVisible: true },
  { key: 'alerts', label: 'Alerts', defaultVisible: true },
  { key: 'action', label: 'Action', defaultVisible: true },
];

// Elenco delle entità coinvolte con le azioni di contenimento previste. Le azioni sono
// disabilitate finché non sarà disponibile il motore di risposta (Fase 3 - SOAR).
export default function EntityFixing({ nodes }: { nodes: GraphNode[] }) {
  const { orderedDefs, moveColumn } = useColumnPrefs('soc.columns.entity_fixing', ENTITY_COLUMNS);
  if (nodes.length === 0) return <p className="text-sm text-gray-400 p-4">No entity extracted for this incident.</p>;

  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-3 text-xs text-gray-500 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
        <Lock size={13} className="text-amber-500" />
        Containment actions require the response engine, planned for Phase 3 (SOAR).
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr>
            {orderedDefs.map(d => (
              <DraggableTh key={d.key} colKey={d.key} onMove={moveColumn}
                className={`table-header ${d.key === 'action' ? 'text-right' : ''}`}>
                {d.label}
              </DraggableTh>
            ))}
          </tr>
        </thead>
        <tbody>
          {nodes.map(n => {
            const Icon = TYPE_ICON[n.type];
            const action = n.type === 'ip' ? 'Block IP' : n.type === 'host' ? 'Isolate host' : n.type === 'user' ? 'Disable account' : n.type === 'process' ? 'Quarantine' : null;
            return (
              <tr key={n.id} className="border-t border-gray-100">
                {orderedDefs.map(d => (
                  <td key={d.key} className={`table-cell ${d.key === 'entity' ? 'font-medium text-gray-800 flex items-center gap-2' : ''} ${d.key === 'type' || d.key === 'alerts' ? 'text-gray-500' : ''} ${d.key === 'action' ? 'text-right' : ''}`}>
                    {d.key === 'entity' && (<><Icon size={14} className="text-gray-400" /> {n.label}</>)}
                    {d.key === 'type' && TYPE_LABEL[n.type]}
                    {d.key === 'exposure' && (n.external
                      ? <span className="badge bg-red-100 text-red-700">External</span>
                      : <span className="badge bg-gray-100 text-gray-600">Internal</span>)}
                    {d.key === 'alerts' && n.alertCount}
                    {d.key === 'action' && (action ? (
                      <button disabled title="Available with the response engine (Phase 3)"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-gray-300 bg-gray-50 cursor-not-allowed">
                        <ShieldOff size={12} /> {action}
                      </button>
                    ) : (
                      <span className="text-xs text-gray-300">reporting source</span>
                    ))}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
